import React from 'react';
import { Box, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';

interface BadgeMenuSelectProps {
  /** Valeur actuelle */
  value: string;
  /** Valeurs proposées dans le menu */
  options: readonly string[];
  /** Couleur associée à une valeur */
  getColor: (valeur: string) => string;
  /** Appelé quand l'utilisateur choisit une autre valeur */
  onChange: (nouvelleValeur: string) => Promise<void> | void;
  /** Libellé affiché au repos (défaut : la valeur) */
  label?: string;
  /** Libellé d'une option dans le menu (défaut : l'option) */
  libelleOption?: (option: string) => string;
  /** L'option est-elle la valeur actuelle (coche) ? Défaut : égalité stricte */
  estSelectionnee?: (option: string, value: string) => boolean;
  /** Infobulle du badge */
  title?: string;
  /** Taille du texte affiché au repos (px) */
  fontSize?: number;
}

/**
 * Badge cliquable : au repos, affiche la valeur colorée ; au clic, ouvre un menu color-codé pour la changer
 * directement dans la liste. Utilisé pour l'état (EtatVisaSelect) et le paiement (EtatPaiementSelect) d'un dossier.
 */
const BadgeMenuSelect: React.FC<BadgeMenuSelectProps> = ({
  value,
  options,
  getColor,
  onChange,
  label,
  libelleOption = (option) => option,
  estSelectionnee = (option, v) => option === v,
  title,
  fontSize = 14
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [saving, setSaving] = React.useState(false);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (saving) return;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = async (e: React.MouseEvent, nouvelleValeur: string) => {
    e.stopPropagation();
    setAnchorEl(null);
    if (nouvelleValeur === value) return;
    try {
      setSaving(true);
      await onChange(nouvelleValeur);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box
        component="span"
        onClick={handleOpen}
        title={title}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '2px',
          cursor: saving ? 'default' : 'pointer',
          color: getColor(value),
          fontWeight: 'bold',
          fontSize: `${fontSize}px`,
          whiteSpace: 'nowrap',
          borderRadius: '4px',
          px: '4px',
          mx: '-4px',
          transition: 'background-color 120ms',
          '&:hover': saving ? {} : { backgroundColor: 'rgba(0,0,0,0.06)' },
          '&:hover .etat-caret': { opacity: 0.9 }
        }}
      >
        {label ?? value}
        {saving ? (
          <CircularProgress size={12} sx={{ ml: '2px', color: getColor(value) }} />
        ) : (
          <ArrowDropDownIcon
            className="etat-caret"
            sx={{ fontSize: 16, opacity: 0.4, transition: 'opacity 120ms' }}
          />
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { maxHeight: 360 } }}
      >
        {options.map((option) => {
          const selected = estSelectionnee(option, value);
          return (
            <MenuItem
              key={option}
              selected={selected}
              onClick={(e) => handleSelect(e, option)}
              sx={{ fontSize: 13, py: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: getColor(option)
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={libelleOption(option)}
                primaryTypographyProps={{
                  sx: { fontWeight: selected ? 'bold' : 'normal', color: getColor(option) }
                }}
              />
              {selected && <CheckIcon sx={{ fontSize: 16, ml: 1, color: getColor(option) }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default BadgeMenuSelect;
